import Moment from 'moment';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Matches an ISO value with no explicit UTC designator or offset (e.g. '2027-01-05', '2027-01-05T00:00:00')
const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}(T[\d:.]+)?$/;

// Matches the 'D MMM YYYY' string the DatePicker emits
const DISPLAY_DATE = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/;

// Format a Date in the expected display format (D MMM YYYY), using the local calendar day.
export const formatDate = (date: Date): string => `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

// Format a date that may arrive as an ISO/UTC string or Date; returns '' when not parseable.
// ISO values without an offset are stored as UTC by the API, so they are read back in UTC —
// otherwise a date-only value at UTC midnight would shift a day in a positive-offset timezone.
export const formatCurrentDate = (date: string | Date | undefined | null): string => {
    if (!date) return '';

    const normalized =
        typeof date === 'string' && ISO_WITHOUT_OFFSET.test(date)
            ? `${date.includes('T') ? date : `${date}T00:00:00`}Z`
            : date;

    const parsed = normalized instanceof Date ? normalized : new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return '';

    // openingDate is non-nullable server side, so an unset one arrives as DateTime.MinValue
    // (0001-01-01) rather than null. Treat that sentinel as "no date", the same way
    // getDateValue in @Helpers/reports blanks MIN_DATE_FORMATTED.
    if (parsed.getUTCFullYear() <= 1) return '';

    return `${parsed.getUTCDate()} ${MONTHS[parsed.getUTCMonth()]} ${parsed.getUTCFullYear()}`;
};

// Parse a 'D MMM YYYY' string into a local-midnight Date; returns an Invalid Date when it doesn't match.
export const parseDisplayDate = (value: string): Date => {
    const match = DISPLAY_DATE.exec(value.trim());
    if (!match) return new Date(NaN);

    const [, day, month, year] = match;
    const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === month.toLowerCase());
    if (monthIndex === -1) return new Date(NaN);

    const parsed = new Date(Number(year), monthIndex, Number(day));
    // Reject overflowed days (e.g. '31 Feb 2027' would otherwise roll into March)
    return parsed.getDate() === Number(day) ? parsed : new Date(NaN);
};

export const daysToDaysWeeks = (days: number | null | undefined): string | null => {
    if (days === undefined || days === null) {
        return null;
    }

    if (days === 0) {
        return "0d";
    }

    const weeks = Math.trunc(days / 7);

    if (weeks > 0) {
        return weeks + "w " + Math.round(days % 7) + "d";
    }
    else {
        return days + "d";
    }

}

export const createdFromNow = (createdDate: string | Date): string => {
    const currentUtcDate = Moment.utc();
    return Moment.utc(createdDate).from(currentUtcDate);
}

export const getRandomHourOfDay = (): number => {
    const min = Math.ceil(1);
    const max = Math.floor(24);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const getRandomDayOfWeek = (): number => {
    const min = Math.ceil(1);
    const max = Math.floor(7);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
